import Highcharts from "Static/js/highstock";
import sortBy from "lodash/sortBy";
import moment from "moment";
import PropTypes from "prop-types";
import React from "react";
import { Button } from "react-bootstrap";
import { Notification } from "ui/notification";
import { Timeline, Separator, Item } from "ui/timeline";
import { utcToLocalFormat, utcToLocal } from "util/time";
import { Box } from "view/layout/box";

import {
  COLOR_CODE_MAP,
  TYPE_ACTION_COLOR_MAP,
  TYPE_ACTION_ICON_MAP,
  TYPE_ACTION_NAME_MAP,
} from "./constants";

class DeviceTimeline extends React.PureComponent {
  static get propTypes() {
    return {
      events: PropTypes.array.isRequired,
      fetchTimeline: PropTypes.func.isRequired,
      storesLoaded: PropTypes.bool.isRequired,
      unloadTimeline: PropTypes.func.isRequired,
      deviceId: PropTypes.string,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      canLoadMore: true,
      chartSeries: [],
      eventGroups: [],
      firstEventId: undefined,
      isLoading: false,
      lastEventId: undefined,
      loadMessage: "",
    };

    this._fetch = this._fetch.bind(this);
    this._onMore = this._onMore.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.events !== this.props.events) {
      this._parseEvents(this.props);
    }
  
    if (prevProps.deviceId !== this.props.deviceId) {
      this.props.fetchTimeline({ deviceId: this.props.deviceId });
    }
  }

  componentDidMount() {
    const { deviceId, storesLoaded } = this.props;
    this._parseEvents(this.props);

    if (storesLoaded) {
      this.props.fetchTimeline({ deviceId });
    }
  }

  componentWillUnmount() {
    if (this.state.chart) {
      this.state.chart.destroy();
    }

    this.props.unloadTimeline();
  }

  _parseKey(key) {
    const [type, action, recovered] = key.split("|");
    return {
      crash_recovered: recovered !== "false",
      type,
      action,
    };
  }

  _parseEvents(props) {
    const { events } = props;
    if (!events.length) {
      return;
    }

    let firstEventId;
    let lastEventId;
    const eventGroupsByDate = {};
    const chartData = {};

    events.forEach((event) => {
      const {
        action,
        id,
        timestamps,
        type,
        crash_recovered: recovered,
        event_date: eventDate,
      } = event;

      if (!firstEventId || firstEventId > id) {
        firstEventId = id;
      }

      if (!lastEventId || lastEventId < id) {
        lastEventId = id;
      }

      const groupDate = utcToLocalFormat(eventDate, "MMMM, YYYY");
      if (!(groupDate in eventGroupsByDate)) {
        eventGroupsByDate[groupDate] = {
          date: eventDate,
          group: groupDate,
          events: [],
        };
      }

      eventGroupsByDate[groupDate].events.push(event);

      timestamps.forEach((timestamp) => {
        const time = moment.utc(timestamp);
        const local = time.local().startOf("hour").unix() * 1000;

        const crashed = recovered !== false;
        const key = `${type}|${action}|${crashed}`;

        if (!(key in chartData)) {
          chartData[key] = {};
        }

        if (local in chartData[key]) {
          chartData[key][local] += 1;
        } else {
          chartData[key][local] = 1;
        }
      });
    });

    const chartSeries = Object.entries(chartData).map(([key, tsMap]) => {
      const series = Object.entries(tsMap).map(([ts, cnt]) => {
        return [Number(ts), cnt];
      });

      return {
        color: this._actionColor(key),
        data: sortBy(series, (el) => el[0]),
        name: this._actionName(key),
      };
    });

    this.setState({
      chartSeries,
      firstEventId,
      lastEventId,
      eventGroups: this._sortEventGroups(eventGroupsByDate),
      isLoading: false,
    });
  }

  _sortEventGroups(eventGroups) {
    const sortedGroups = sortBy(
      Object.values(eventGroups),
      (e) => e.date,
    ).reverse();

    sortedGroups.forEach((group) => {
      group.events = sortBy(group.events, (e) => e.event_date).reverse();
    });

    return sortedGroups;
  }

  _onMore() {
    this.setState({ isLoading: true }, this._fetch);
  }

  _fetch() {
    const { firstEventId, lastEventId } = this.state;
    const { deviceId, fetchTimeline } = this.props;

    fetchTimeline({
      deviceId,
      firstEventId,
      lastEventId,
    })
      .then((events) => {
        const { data } = events;
        if (!data || !data.length) {
          this.setState({
            canLoadMore: false,
            loadMessage: "There are no other events available.",
          });
        }
      })
      .catch(() => {
        this.setState({
          loadMessage: "Failed to load events. Please try again.",
        });
      })
      .finally(() => {
        this.setState({
          isLoading: false,
        });
      });
  }

  _renderMoreButton() {
    const { loadMessage, isLoading, canLoadMore } = this.state;
    return (
      <li className="timeline-container">
        <div key="more-button" className="timeline-item">
          <Notification warning={loadMessage} />
          <Button
            block
            variant="primary"
            size="large"
            onClick={this._onMore}
            disabled={isLoading || !canLoadMore}
          >
            Load More
          </Button>
        </div>
      </li>
    );
  }

  _renderEventGroups() {
    return this.state.eventGroups.map((eventGroup) => {
      const { group: date, events } = eventGroup;
      return [
        <Separator key={`sep-${date}`} color="aqua" text={`${date}`} />,
        ...this._renderEventGroup(events),
      ];
    });
  }

  _renderEventGroup(events) {
    return events.map((e) => {
      return (
        <Item
          key={e.id}
          color={this._color(e)}
          content={this._desc(e)}
          icon={this._icon(e)}
          time={this._localDateTime(e)}
          title={e.title}
        />
      );
    });
  }

  _localDateTime(event) {
    return utcToLocalFormat(event.event_date, "MMM Do, YYYY hh:mm:ss a");
  }

  _desc(event) {
    const { desc, timestamps = [] } = event;

    if (timestamps.length === 2) {
      return `${desc} This event happened one more time recently.`;
    } else if (timestamps.length > 2) {
      return `${desc} This event happened ${timestamps.length - 1}
        more times recently.`;
    }

    return desc;
  }

  _getFormattedValue(event, formattedActionMap, defaultVal) {
    const { action, type } = event;
    const processTerminated = event.crash_recovered !== false;
    let val = defaultVal;

    const actions = formattedActionMap[type];
    if (!actions || !actions[action]) {
      return val;
    }

    val = actions[action];
    if (type === "INAPP" && action === "CRASH") {
      val = processTerminated ? val[0] : val[1];
    }

    return val;
  }

  _color(event) {
    return this._getFormattedValue(event, TYPE_ACTION_COLOR_MAP, "blue");
  }

  _icon(event) {
    return this._getFormattedValue(event, TYPE_ACTION_ICON_MAP, "check-square");
  }

  _actionName(key) {
    const event = this._parseKey(key);
    return this._getFormattedValue(event, TYPE_ACTION_NAME_MAP, "Unknown");
  }

  _actionColor(key) {
    const event = this._parseKey(key);
    const color = this._color(event);
    return COLOR_CODE_MAP[color];
  }

  render() {
    const { chartSeries = {} } = this.state;

    return (
      <Box type="default" title="Device Timeline">
        <TimelineChart data={chartSeries} />
        <Timeline>
          {this._renderEventGroups()}
          {this._renderMoreButton()}
        </Timeline>
      </Box>
    );
  }
}

class TimelineChart extends React.PureComponent {
  static get propTypes() {
    return {
      data: PropTypes.array,
    };
  }

  static get defaultProps() {
    return {
      data: [],
    };
  }

  constructor(props) {
    super(props);
    this.state = {
      chart: undefined,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data) {
      this._renderChart(this.props);
    }
  }

  componentDidMount() {
    this._renderChart(this.props);
  }

  _renderChart(props) {
    if (!props.data) {
      return;
    }

    const node = this.chart;
    const opts = {
      chart: {
        renderTo: node,
        type: "column",
      },
      xAxis: {
        type: "datetime",
        labels: {
          enabled: true,
        },
        title: {
          enabled: false,
        },
        gridLineWidth: 0,
      },
      title: {
        text: "",
        style: {
          display: "none",
        },
      },
      tooltip: {
        // Don't use arrow function since we don't want to bind this
        // to the component
        formatter: function () {
          const eventTime = utcToLocal(this.x);
          if (this.y > 1) {
            return `${this.point.series.name}: ${this.y}
              Events<br>${eventTime}`;
          }
          return `${this.point.series.name}: ${this.y} Event<br>${eventTime}`;
        },
        shared: false,
      },
      plotOptions: {
        series: {
          connectNulls: false,
          // Allow per-minute charts
          turboThreshold: 48 * 60,
        },
        column: {
          stacking: "normal",
          pointWidth: 15,
        },
      },
      yAxis: {
        title: null,
        labels: {
          enabled: false,
        },
        type: "category",
        gridLineWidth: 0,
        minorGridLineWidth: 0,
        startOnTick: false,
        showFirstLabel: true,
        showLastLabel: true,
      },
      series: props.data,
      credits: {
        enabled: false,
      },
    };

    this.setState({ chart: new Highcharts.Chart(opts) });
  }

  render() {
    return (
      <div
        className="chart event-chart"
        ref={(c) => {
          this.chart = c;
        }}
      ></div>
    );
  }
}

export { DeviceTimeline };
